import graphql__085ce9e4c2b772e26dc6 from "./__generated__/HomePage_platform.graphql.js";
import graphql__0ef9d58db9c0de3ce1ba from "./__generated__/HomePage_homePage.graphql.js";
import graphql__ac40991a029c21278f05 from "./__generated__/HomePage_navigationItems.graphql.js";
import graphql__7cdc8909265d6f56bba4 from "./__generated__/HomePage_authenticationConfig.graphql.js";
import graphql__618cb838a88ca602412b from "./__generated__/HomePage_viewer.graphql.js";import { useCallback, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { graphql, useFragment } from "react-relay";
import { useDispatch } from "react-redux";

import SimpleMenuMainNavigation from "./MainNavigation/SimpleMenu/SimpleMenuMainNavigation";
import AdminLink from "./Header/AdminLink";
import PageSections from "./PageSection/PageSections";
import PageTitle from "./Pages/PageTitle";
import AnalyticsDataTextRow from "./AnalyticsDatum/AnalyticsDataTextRow";
import PushableContainer from "./Utils/PushableContainer";
import { Desktop, useResponsiveFlag } from "./Utils/ResponsiveQueries";
import Subscription from "./Subscriptions/Subscription";
import { useFormatMessage } from "./Intl/Context";
import { intlMessages as headerIntlMessages } from "./Header/Header";
import { useAppContainer } from "./Main/AppContainerContext";

import { setHeaderShown } from "../reducers/styling";

const HomePage = props => {
  const platform = useFragment(HomePage_platform, props.platform);
  const homePage = useFragment(HomePage_homePage, props.homePage);
  const navigationItems = useFragment(HomePage_navigationItems, props.navigationItems);
  const authenticationConfig = useFragment(
    HomePage_authenticationConfig,
    props.authenticationConfig
  );
  const viewer = useFragment(HomePage_viewer, props.viewer);
  const { title, subtitle, homeLogoBackgroundColor, logos, enableComplexMenu } = platform;
  const { analyticsData } = homePage;

  const appContainer = useAppContainer();

  const headerContainer = useMemo(() => {
    const containerNode = document.createElement("div");
    containerNode.className = "home-header-container";

    return containerNode;
  }, []);

  const { isDesktop } = useResponsiveFlag();

  const t = useFormatMessage();
  const home = t(headerIntlMessages.home);
  const altText = [title, home].join(", ");
  const menuAriaLabel = t(headerIntlMessages.mainMenu);

  const titleContent = useMemo(
    () => (
      <>
        <span className="d-block text-title-main-opening">{title}</span>
        <span className="d-block text-subtitle-main-opening">{subtitle}</span>
      </>
    ),
    [subtitle, title]
  );

  const getIsScrolled = useCallback(() => {
    const homeHeader = document.querySelector(".home-header");
    const homeMenu = document.querySelector(".home-menu-navbar-wrapper");

    const scrollOffset =
      homeHeader && homeMenu ? homeHeader.offsetHeight + homeMenu.offsetHeight : 200;
    const offsetY = window.pageYOffset || document.documentElement.scrollTop;

    return offsetY >= scrollOffset;
  }, []);

  const dispatch = useDispatch();

  const updateHeaderShown = useCallback(() => {
    const showHeader = !isDesktop || getIsScrolled();

    dispatch(setHeaderShown(showHeader));
  }, [dispatch, getIsScrolled, isDesktop]);

  useEffect(() => {
    window.addEventListener("scroll", updateHeaderShown);
    updateHeaderShown();

    return () => {
      dispatch(setHeaderShown(true));
      window.removeEventListener("scroll", updateHeaderShown);
    };
  }, [dispatch, updateHeaderShown]);

  useEffect(() => {
    updateHeaderShown();
  }, [updateHeaderShown]);

  useEffect(() => {
    if (isDesktop) appContainer.prepend(headerContainer);

    return () => {
      if (isDesktop) appContainer.removeChild(headerContainer);
    };
  }, [appContainer, headerContainer, isDesktop]);

  // Avoid having a blank container that has margins when nothing is shown underneath the
  // title
  const displayTitleInformations = !enableComplexMenu && analyticsData.length > 0;

  const header = useMemo(
    () => (
      <PushableContainer className="home">
        <AdminLink />

        <header role="banner" className="home-header container margin-y-xs">
          <div className="row">
            <div className="col-md-10">
              <div className="box box-page-title d-flex align-items-center padding-none">
                <div className="flex-1">
                  <PageTitle
                    page={homePage}
                    title={titleContent}
                    shareButtons={false}
                    subscribable={false}
                    breadcrumbs={false}
                    includePlatformTitleInHeadTitle={false}
                  >
                    {displayTitleInformations && (
                      <div className="d-flex margin-top-xs">
                        {!enableComplexMenu && (
                          <Subscription
                            subscribable={homePage}
                            className="d-flex margin-right-sm"
                          />
                        )}

                        <AnalyticsDataTextRow analyticsData={analyticsData} />
                      </div>
                    )}
                  </PageTitle>
                </div>

                {enableComplexMenu && (
                  <Subscription
                    subscribable={homePage}
                    className="d-flex margin-right-md"
                    hideLabel
                  />
                )}
              </div>
            </div>

            <Desktop>
              <div className="col-md-2">
                <div
                  className="box d-flex align-items-center h-100 padding-xs"
                  style={{
                    backgroundColor: homeLogoBackgroundColor
                  }}
                >
                  <img src={logos.home} alt={altText} className="w-100" />
                </div>
              </div>
            </Desktop>
          </div>
        </header>

        {!enableComplexMenu && (
          <nav
            role="navigation"
            className="container margin-bottom-xs home-menu-navbar-wrapper navbar-menu-container"
            aria-label={menuAriaLabel}
          >
            <SimpleMenuMainNavigation
              navigationItems={navigationItems}
              authenticationConfig={authenticationConfig}
              viewer={viewer}
              platform={platform}
              dispatchBackdropOpen={true}
            />
          </nav>
        )}
      </PushableContainer>
    ),
    [
      altText,
      analyticsData,
      authenticationConfig,
      displayTitleInformations,
      enableComplexMenu,
      homeLogoBackgroundColor,
      homePage,
      logos.home,
      menuAriaLabel,
      navigationItems,
      platform,
      titleContent,
      viewer
    ]
  );

  return (
    <>
      {/* With the legacy simple menu, the home header is only displayed on desktop sized 
          devices, as the mobile top header always includes the platform name */}
      {!enableComplexMenu && (
        <Desktop>{ReactDOM.createPortal(header, headerContainer)}</Desktop>
      )}

      {/* With the new complex menu, we display the header on both desktop and mobile */}
      {enableComplexMenu && header}

      <PageSections page={homePage} />
    </>
  );
};

const HomePage_platform = graphql__085ce9e4c2b772e26dc6;

const HomePage_homePage = graphql__0ef9d58db9c0de3ce1ba;

const HomePage_navigationItems = graphql__ac40991a029c21278f05;

const HomePage_authenticationConfig = graphql__7cdc8909265d6f56bba4;

const HomePage_viewer = graphql__618cb838a88ca602412b;

export default HomePage;
